// useMentionsFeed — read-path hook for the Mentions activity view.

import { useMemo } from "react";

import { useMatrix } from "~/context/MatrixContext";
import { useRooms } from "~/hooks/useRooms";
import type { MentionFeedItem } from "~/lib/matrix";

export type MentionItem = Extract<MentionFeedItem, { kind: "mention" }>;

export function isMentionItem(item: MentionFeedItem): item is MentionItem {
  return item.kind === "mention";
}

export interface UseMentionsFeedResult {
  items: MentionItem[];
  clearAll: () => Promise<void>;
}

export function useMentionsFeed(): UseMentionsFeedResult {
  const { mentionsFeed, clearMentions } = useMatrix();
  const rooms = useRooms();
  const visibleItems = useMemo(() => {
    const visibleRoomIds = new Set(rooms.map((room) => room.id));
    return (mentionsFeed ?? []).filter((item) => visibleRoomIds.has(item.roomId));
  }, [mentionsFeed, rooms]);

  const items = useMemo(
    () => visibleItems.filter((item): item is MentionItem => isMentionItem(item)),
    [visibleItems],
  );

  return {
    items,
    clearAll: clearMentions ? () => clearMentions(visibleItems) : async () => {},
  };
}
